import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { behaviorAPI, adminAPI } from '../../services/api';
import CompetitorCostPanel from '../../components/CompetitorCostPanel';

/**
 * User Insights — NEW admin tab (Phase 0-1 of behavior analytics). Shows the
 * event funnel, RFM lifecycle segments, and discount-sensitivity buckets, plus a
 * segmented user table. Purely additive; reads only the new analytics endpoints.
 */

const SEG_LABELS = {
  champion: 'Champions', loyal: 'Loyal', potential_loyalist: 'Potential Loyalists',
  new: 'New', promising: 'Promising', needs_attention: 'Needs Attention',
  at_risk: 'At Risk', cant_lose: "Can't Lose Them", hibernating: 'Hibernating',
  lost: 'Lost', never_ordered: 'Never Ordered',
};
const DISC_LABELS = {
  discount_dependent: 'Discount-Dependent (only order with a discount)',
  discount_leaning: 'Discount-Leaning',
  mixed: 'Mixed',
  full_price: 'Full-Price (order without discounts)',
  unknown: 'Unknown (too few orders)',
};
const DISC_COLOR = {
  discount_dependent: '#EF4444', discount_leaning: '#F59E0B', mixed: '#6366F1',
  full_price: '#059669', unknown: '#9CA3AF',
};

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n) => (n ?? 0).toLocaleString('en-IN');
const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
const th = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', fontSize: 13, color: '#555' };
const td = { padding: '10px 12px', borderBottom: '1px solid #f2f2f2', fontSize: 14 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14 });

function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { startDate: iso(start), endDate: iso(now) };
}

function UserInsights() {
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState(monthRange());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const [tracking, setTracking] = useState(false);
  const [overview, setOverview] = useState(null);
  const [funnelStep, setFunnelStep] = useState(null);   // { key, step } drilled into
  const [stepData, setStepData] = useState(null);
  const [stepLoading, setStepLoading] = useState(false);
  // Cart → Zomato/Swiggy comparison (modal for one customer's live cart)
  const [compUser, setCompUser] = useState(null);   // { userId, name } currently shown
  const [compCost, setCompCost] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compErr, setCompErr] = useState('');
  const [segments, setSegments] = useState(null);
  const [opens, setOpens] = useState(null);
  const [openDays, setOpenDays] = useState(30);

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ segment: '', discountSensitivity: '' });

  const loadTracking = useCallback(async () => {
    try { const r = await behaviorAPI.getTracking(); setTracking(r.data.data.enabled); } catch (e) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'overview') { const r = await behaviorAPI.getOverview(range); setOverview(r.data.data); }
      if (tab === 'segments') { const r = await behaviorAPI.getSegments(); setSegments(r.data.data); }
      if (tab === 'users') {
        const r = await behaviorAPI.getUsers({ ...filters, page: pagination.page, limit: pagination.limit });
        setUsers(r.data.data.users); setPagination(r.data.data.pagination);
      }
      if (tab === 'notifications') { const r = await behaviorAPI.getNotificationOpens(openDays); setOpens(r.data.data); }
    } catch (e) { setMsg(e.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [tab, range, filters, pagination.page, pagination.limit, openDays]);

  useEffect(() => { loadTracking(); }, [loadTracking]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, range, filters, pagination.page, openDays]);

  const toggleTracking = async () => {
    try { const r = await behaviorAPI.setTracking(!tracking); setTracking(r.data.data.enabled); setMsg(`Tracking ${r.data.data.enabled ? 'ENABLED' : 'disabled'}`); }
    catch (e) { setMsg('Failed to toggle tracking'); }
  };
  const recompute = async () => {
    setMsg('Recomputing segments…');
    try { const r = await behaviorAPI.recompute(); setMsg(r.data.message); if (tab === 'segments' || tab === 'users') load(); }
    catch (e) { setMsg(e.response?.data?.message || 'Recompute failed'); }
  };

  // Drill a funnel step → the customers (name + phone) who did that action.
  const loadStepPage = async (f, page) => {
    setFunnelStep(f); setStepLoading(true);
    try {
      const r = await behaviorAPI.getEventUsers({ type: f.key, ...range, page, limit: 25 });
      setStepData(r.data.data);
    } catch (e) { setMsg(e.response?.data?.message || 'Failed to load users'); setStepData(null); }
    finally { setStepLoading(false); }
  };
  const toggleFunnelStep = (f) => {
    if (funnelStep && funnelStep.key === f.key) { setFunnelStep(null); setStepData(null); }
    else loadStepPage(f, 1);
  };
  // Open the Zomato/Swiggy comparison modal for one customer's live cart.
  const openCartCompare = async (u) => {
    setCompUser({ userId: u.userId, name: u.name });
    setCompCost(null); setCompErr(''); setCompLoading(true);
    try {
      const res = await adminAPI.getCartCompetitorCost(u.userId);
      setCompCost(res.data?.data || null);
    } catch (e) {
      setCompErr(e.response?.data?.message || 'Failed to load competitor prices');
    } finally { setCompLoading(false); }
  };

  const maxFunnel = overview?.funnel?.[0]?.count || 1;

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 4 }}>📊 User Insights</h1>
        <p style={{ color: '#777', marginTop: 0 }}>Behavior funnel, RFM lifecycle segments, and discount-sensitivity.</p>

        {/* Controls */}
        <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={tracking} onChange={toggleTracking} />
            <b>Behavior Tracking {tracking ? 'ON' : 'OFF'}</b>
          </label>
          <span style={{ fontSize: 12, color: '#999' }}>
            {tracking ? 'App events are being collected.' : 'Off — enable once the app build that sends events is live + a privacy notice is in place.'}
          </span>
          <button onClick={recompute} style={{ ...btn('#6366F1'), marginLeft: 'auto' }}>↻ Recompute Segments</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['overview', 'segments', 'users', 'notifications'].map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{ ...btn(tab === t ? '#FF6961' : '#eee'), color: tab === t ? '#fff' : '#333', textTransform: 'capitalize' }}>{t === 'notifications' ? 'Push Opens' : t}</button>
          ))}
        </div>

        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669', marginBottom: 16 }}>{msg}</div>}

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div>
            <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div><label style={{ display: 'block', fontSize: 12, color: '#666' }}>From</label>
                <input type="date" value={range.startDate} onChange={(e) => setRange({ ...range, startDate: e.target.value })} /></div>
              <div><label style={{ display: 'block', fontSize: 12, color: '#666' }}>To</label>
                <input type="date" value={range.endDate} onChange={(e) => setRange({ ...range, endDate: e.target.value })} /></div>
            </div>
            {loading ? <div style={{ padding: 16 }}>🔄 Loading…</div> : overview && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                  <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Active Users</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(overview.activeUsers)}</div></div>
                  <div style={card}><div style={{ color: '#777', fontSize: 12 }}>App Opens</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(overview.counts?.app_open)}</div><div style={{ fontSize: 11, color: '#999' }}>{num(overview.uniqueUsersByType?.app_open)} users</div></div>
                  <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Add to Cart</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(overview.counts?.add_to_cart)}</div><div style={{ fontSize: 11, color: '#999' }}>{num(overview.uniqueUsersByType?.add_to_cart)} users</div></div>
                  <div style={card}><div style={{ color: '#777', fontSize: 12 }}>Cart→Order Rate</div><div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{overview.cartToOrderRate != null ? `${overview.cartToOrderRate}%` : '—'}</div></div>
                  <div
                    style={{ ...card, cursor: 'pointer', borderColor: '#FCA5A5' }}
                    title="Registered customers who have never placed an order — click to see the list"
                    onClick={() => { setFilters({ segment: 'never_ordered', discountSensitivity: '' }); setPagination((p) => ({ ...p, page: 1 })); setTab('users'); }}
                  >
                    <div style={{ color: '#777', fontSize: 12 }}>Never Ordered ▸</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#DC2626' }}>{num(overview.neverOrdered)}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {overview.neverOrderedPct != null ? `${overview.neverOrderedPct}% of ${num(overview.registeredCustomers)} registered` : ''}
                    </div>
                  </div>
                </div>
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>Conversion Funnel <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>(click a step to see the customers · count = total times, users = distinct customers)</span></h3>
                  {overview.funnel.map((f) => {
                    const active = funnelStep && funnelStep.key === f.key;
                    return (
                    <div key={f.key} onClick={() => toggleFunnelStep(f)} title="Click to see who did this"
                      style={{ marginBottom: 10, cursor: 'pointer', borderRadius: 8, padding: active ? 8 : 0, background: active ? '#FFF4F3' : 'transparent' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span>{f.step} <span style={{ fontSize: 11, color: '#c0392b' }}>{active ? '▾' : '▸ who?'}</span></span>
                        <span style={{ textAlign: 'right' }}><b>{num(f.count)}</b> <span style={{ fontSize: 11, color: '#777', fontWeight: 400 }}>times</span> · <b style={{ color: '#2563EB' }}>{num(f.users)}</b> <span style={{ fontSize: 11, color: '#777', fontWeight: 400 }}>users</span></span>
                      </div>
                      <div style={{ background: '#f0f0f0', borderRadius: 6, height: 18 }}>
                        <div style={{ width: `${Math.min(100, (f.count / maxFunnel) * 100)}%`, background: '#FF6961', height: '100%', borderRadius: 6 }} />
                      </div>
                    </div>
                    );
                  })}
                  {(!overview.counts?.app_open) && <p style={{ color: '#999', fontSize: 12 }}>No events yet — enable tracking and ship the app build that sends events.</p>}

                  {/* Drill-down: the customers who performed the selected step */}
                  {funnelStep && (
                    <div style={{ marginTop: 8, border: '1px solid #eee', borderRadius: 8, padding: 12, background: '#fafafa' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <b>{funnelStep.step}</b>
                        {stepData && <span style={{ fontSize: 12, color: '#777' }}>{num(stepData.distinctUsers)} customer(s){stepData.guestEvents > 0 ? ` · ${num(stepData.guestEvents)} guest events` : ''}</span>}
                        <button onClick={() => { setFunnelStep(null); setStepData(null); }} style={{ ...btn('#eee'), color: '#333', padding: '4px 10px', marginLeft: 'auto' }}>Close</button>
                      </div>
                      {stepLoading ? <div style={{ fontSize: 13, color: '#888' }}>Loading…</div> : stepData && (
                        stepData.users.length === 0
                          ? <div style={{ fontSize: 13, color: '#888' }}>No signed-in customers for this step in the selected date range.</div>
                          : (<>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead><tr style={{ textAlign: 'left', borderBottom: '2px solid #eee', color: '#555' }}>
                                  <th style={{ padding: '5px 8px' }}>Customer</th>
                                  <th style={{ padding: '5px 8px' }}>Phone</th>
                                  <th style={{ padding: '5px 8px', textAlign: 'right' }}>Times</th>
                                  <th style={{ padding: '5px 8px' }}>Last</th>
                                  {['add_to_cart', 'item_view', 'remove_from_cart'].includes(funnelStep.key) && (
                                    <th style={{ padding: '5px 8px' }}>{funnelStep.key === 'item_view' ? 'Items viewed' : funnelStep.key === 'remove_from_cart' ? 'Items removed' : 'Items in cart'}</th>
                                  )}
                                  {funnelStep.key === 'add_to_cart' && (
                                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>Grand total</th>
                                  )}
                                </tr></thead>
                                <tbody>
                                  {stepData.users.map((u) => (
                                    <tr key={u.userId} style={{ borderBottom: '1px solid #f2f2f2' }}>
                                      <td style={{ padding: '5px 8px' }}>{u.name || '—'}</td>
                                      <td style={{ padding: '5px 8px' }}>{u.phone || '—'}</td>
                                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{num(u.events)}</td>
                                      <td style={{ padding: '5px 8px', color: '#888' }}>{u.lastAt ? new Date(u.lastAt).toLocaleDateString() : '—'}</td>
                                      {['add_to_cart', 'item_view', 'remove_from_cart'].includes(funnelStep.key) && (
                                        <td style={{ padding: '5px 8px' }}>
                                          {(u.items && u.items.length) ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                              {u.items.map((it, i) => (
                                                <span key={it.menuItemId || i} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                                                  <span style={{ fontWeight: 500 }}>{it.name}</span>{` ×${it.count}`}{it.price != null ? ` · ₹${it.price}` : ''}{it.restaurant ? <span style={{ color: '#888' }}> · {it.restaurant}</span> : null}
                                                </span>
                                              ))}
                                            </div>
                                          ) : <span style={{ color: '#aaa' }}>—</span>}
                                        </td>
                                      )}
                                      {funnelStep.key === 'add_to_cart' && (
                                        <td style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                          {u.grandTotal != null ? (
                                            <span><b>₹{num(Math.round(u.grandTotal))}</b><span style={{ fontSize: 10, color: '#aaa', marginLeft: 4 }}>{u.cartLive ? 'live' : 'est.'}</span></span>
                                          ) : <span style={{ color: '#aaa' }}>—</span>}
                                          {u.cartLive && (
                                            <div style={{ marginTop: 4 }}>
                                              <button onClick={() => openCartCompare(u)} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }} title="What this cart would cost on Zomato/Swiggy">🍽️ vs Zomato/Swiggy</button>
                                            </div>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {stepData.pagination.pages > 1 && (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13 }}>
                                <button disabled={stepData.pagination.page <= 1} onClick={() => loadStepPage(funnelStep, stepData.pagination.page - 1)} style={{ ...btn('#eee'), color: '#333', padding: '3px 10px' }}>Prev</button>
                                <span>Page {stepData.pagination.page} / {stepData.pagination.pages}</span>
                                <button disabled={stepData.pagination.page >= stepData.pagination.pages} onClick={() => loadStepPage(funnelStep, stepData.pagination.page + 1)} style={{ ...btn('#eee'), color: '#333', padding: '3px 10px' }}>Next</button>
                              </div>
                            )}
                          </>)
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* PUSH OPENS */}
        {tab === 'notifications' && (
          <div>
            <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: '#666' }}>Window:&nbsp;
                <select value={openDays} onChange={(e) => { setOpens(null); setOpenDays(Number(e.target.value)); }}>
                  <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
                </select>
              </label>
              {opens && <span style={{ fontSize: 13, color: '#777' }}>Total opens: <b>{num(opens.totalOpens)}</b> · <b style={{ color: '#2563EB' }}>{num(opens.totalOpenUsers)}</b> users</span>}
              {opens && !opens.trackingEnabled && <span style={{ fontSize: 12, color: '#B45309' }}>⚠️ Behavior tracking is OFF — opens aren't being stored.</span>}
            </div>
            {loading ? <div style={{ padding: 16 }}>🔄 Loading…</div> : opens && (
              <>
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>Re-engagement open rate <span style={{ fontSize: 12, color: '#999' }}>(opens ÷ sends, approximate)</span></h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee', color: '#555' }}>
                        <th style={{ padding: '6px 8px' }}>Campaign</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sends</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Opens</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Open rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opens.reengagement.map((r) => (
                        <tr key={r.campaign} style={{ borderBottom: '1px solid #f2f2f2' }}>
                          <td style={{ padding: '6px 8px', textTransform: 'capitalize' }}>{r.campaign.replace(/_/g, ' ')}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(r.sends)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(r.opens)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: r.openRate >= 10 ? '#059669' : '#555' }}>{r.openRate != null ? `${r.openRate}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ ...card, marginTop: 16 }}>
                  <h3 style={{ marginTop: 0 }}>Opens by notification type</h3>
                  {opens.opensByType.length === 0
                    ? <p style={{ color: '#999', fontSize: 12 }}>No opens yet — needs behavior tracking ON + the app build (1.0.33+71) that logs notification taps.</p>
                    : opens.opensByType.map((o) => (
                      <div key={o.type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span>{o.type}</span><span><b>{num(o.opens)}</b> opens · {num(o.users)} users</span>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* SEGMENTS */}
        {tab === 'segments' && (loading ? <div style={{ padding: 16 }}>🔄 Loading…</div> : segments && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
            <div style={card}>
              <h3 style={{ marginTop: 0 }}>Discount Sensitivity <span style={{ fontSize: 12, color: '#999' }}>({num(segments.totalUsers)} users)</span></h3>
              <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>Who to give offers to (discount-dependent) vs who orders anyway (full-price).</p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Bucket</th><th style={th}>Users</th><th style={th}>Revenue</th></tr></thead>
                <tbody>
                  {segments.byDiscountSensitivity.map((s) => (
                    <tr key={s.sensitivity}>
                      <td style={td}><span style={{ color: DISC_COLOR[s.sensitivity] || '#333' }}>●</span> {DISC_LABELS[s.sensitivity] || s.sensitivity}</td>
                      <td style={td}>{num(s.count)}</td><td style={td}><b>{fmt(s.revenue)}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={card}>
              <h3 style={{ marginTop: 0 }}>RFM Lifecycle Segments</h3>
              <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>Recency / Frequency / Monetary buckets.</p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Segment</th><th style={th}>Users</th><th style={th}>Revenue</th></tr></thead>
                <tbody>
                  {segments.bySegment.map((s) => (
                    <tr key={s.segment}><td style={td}>{SEG_LABELS[s.segment] || s.segment}</td><td style={td}>{num(s.count)}</td><td style={td}><b>{fmt(s.revenue)}</b></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* USERS */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <select value={filters.segment} onChange={(e) => { setFilters({ ...filters, segment: e.target.value }); setPagination({ ...pagination, page: 1 }); }}>
                <option value="">All segments</option>
                {Object.keys(SEG_LABELS).map((k) => <option key={k} value={k}>{SEG_LABELS[k]}</option>)}
              </select>
              <select value={filters.discountSensitivity} onChange={(e) => { setFilters({ ...filters, discountSensitivity: e.target.value }); setPagination({ ...pagination, page: 1 }); }}>
                <option value="">All discount buckets</option>
                {Object.keys(DISC_LABELS).map((k) => <option key={k} value={k}>{DISC_LABELS[k]}</option>)}
              </select>
            </div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Customer</th><th style={th}>Segment</th><th style={th}>Discount</th>
                  <th style={th}>Orders</th><th style={th}>Spend</th><th style={th}>AOV</th>
                  <th style={th}>Recency</th><th style={th}>Opens30d</th>
                </tr></thead>
                <tbody>
                  {loading && <tr><td style={td} colSpan={8}>🔄 Loading…</td></tr>}
                  {!loading && users.length === 0 && <tr><td style={td} colSpan={8}>No segmented users yet — click "Recompute Segments".</td></tr>}
                  {!loading && users.map((u) => (
                    <tr key={u._id}>
                      <td style={td}>{u.name || '—'}<br /><span style={{ fontSize: 11, color: '#999' }}>{u.phone}</span></td>
                      <td style={td}>{SEG_LABELS[u.segment] || u.segment}</td>
                      <td style={td}><span style={{ color: DISC_COLOR[u.discountSensitivity], fontSize: 12 }}>{DISC_LABELS[u.discountSensitivity]?.split(' (')[0] || u.discountSensitivity}</span></td>
                      <td style={td}>{num(u.totalOrders)}</td>
                      <td style={td}><b>{fmt(u.monetary)}</b></td>
                      <td style={td}>{fmt(u.avgOrderValue)}</td>
                      <td style={td}>{u.recencyDays != null ? `${u.recencyDays}d` : '—'}</td>
                      <td style={td}>{num(u.appOpens30d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button disabled={pagination.page <= 1} onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })} style={btn('#eee')}>‹ Prev</button>
                <span style={{ fontSize: 13 }}>Page {pagination.page} / {pagination.pages} ({num(pagination.total)} users)</span>
                <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })} style={btn('#eee')}>Next ›</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cart → Zomato/Swiggy comparison modal */}
      {compUser && (
        <div onClick={() => setCompUser(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 14px', zIndex: 1000, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 780, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden', marginBottom: 40 }}>
            <div style={{ background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Cart vs Zomato / Swiggy</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>{compUser.name || 'Customer'} · live cart</div>
              </div>
              <button onClick={() => setCompUser(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, fontSize: 16, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 18, maxHeight: '68vh', overflowY: 'auto' }}>
              {compLoading ? <div style={{ color: '#6b7280', fontSize: 13 }}>Matching cart items to competitor menus…</div>
                : compErr ? <div style={{ color: '#dc2626', fontSize: 13 }}>{compErr}</div>
                : <CompetitorCostPanel data={compCost} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserInsights;
